import type { JSX, ReactNode, InputHTMLAttributes } from 'react';

export interface InputGroupProps {
  /**
   * Label text shown on the left side with gray background
   */
  label: string;

  /**
   * Input element props (type, value, onChange, etc.)
   */
  inputProps: InputHTMLAttributes<HTMLInputElement>;

  /**
   * Optional action button or element on the right side
   */
  action?: ReactNode;

  /**
   * Additional className for the container
   */
  className?: string;

  /**
   * Whether to auto-select the input content on focus
   * @default false
   */
  selectAllOnFocus?: boolean;
}

/**
 * InputGroup component - Bootstrap-style input group with label, input, and optional action
 *
 * Features:
 * - Left label section with gray background
 * - Centre input field with focus states
 * - Optional right action section (typically a button)
 * - Single cohesive border with rounded corners
 *
 * @example
 * ```tsx
 * <InputGroup
 *   label="Child 1 Age:"
 *   inputProps={{
 *     type: "number",
 *     min: 0,
 *     max: 18,
 *     value: age,
 *     onChange: (e) => setAge(Number(e.target.value))
 *   }}
 *   action={
 *     <button onClick={handleRemove}>Remove</button>
 *   }
 * />
 * ```
 */
export function InputGroup({
  label,
  inputProps,
  action,
  className = '',
  selectAllOnFocus = false,
}: InputGroupProps): JSX.Element {
  // Auto-select input content on focus if enabled
  // Call user handler first, then select() to maintain expected event flow
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
    inputProps.onFocus?.(e);
    if (selectAllOnFocus) {
      e.target.select();
    }
  };

  return (
    <div className={`flex items-stretch border-2 border-gray-300 rounded-md overflow-hidden focus-within:outline focus-within:outline-1 focus-within:outline-primary focus-within:outline-offset-0 transition-all ${className}`}>
      {/* Label section */}
      <span className="flex items-center px-3 bg-gray-50 text-sm text-gray-700 border-r border-gray-300">
        {label}
      </span>

      {/* Input section */}
      <input
        {...inputProps}
        onFocus={handleFocus}
        className={`flex-1 px-3 py-2 text-sm border-none focus:outline-none ${inputProps.className || ''}`}
      />

      {/* Action section (optional) */}
      {action && (
        <div className="flex items-stretch">
          {action}
        </div>
      )}
    </div>
  );
}
